import { Box } from '@mui/material';
import { useForm, FormProvider, useWatch } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import CustomButton from 'components/buttons/CustomButton/CustomButton';
import { procedureDataType } from 'types/types';
import { useGetApproachList, useGetGenders, useGetIndicationList, useGetInsuranceList, useGetInsuranceStatusList, useGetLocationList, useGetOsteotomyList, useGetPositionList, useGetProcedureList, useGetProcedureTypes, useGetRaceList, useGetRoles, useGetSideList, useGetTICIGrade, useGetVendorsList } from 'hooks/useLookups';
import { useGetUserHospitals } from 'hooks/useUserInfo';
import { useAuth } from 'context/AuthContext';
import { addCaseSchema } from 'validation/caseValidation';
import PatientInformation from './PatientInformation';
import Diagnosis from './Diagnosis';
import { useEffect, useMemo, useState } from 'react';
import SelectICDs from 'components/modals/SelectICD/SelectICDs';
import ProcedureDetails from './ProcedureDetails';
import dayjs from 'dayjs';
import { LabelValueType } from 'types/types/Types';
import { isInterventional, isNeurosurgery } from 'common/caseHelpers';
import CaseProcedures from 'components/case/CaseProcedures/CaseProcedures';
import SelectCPTs from 'components/modals/SelectCPTs/SelectCPTs';
import CaseNotes from 'components/case/CaseNotes/CaseNotes';
import CaseImages from 'components/case/CaseImages/CaseImages';
import { useCreateProcedure, useGetProcedureById, useUpdateProcedure, useUpdateProcedureImages } from 'hooks/useProcedure';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { snackbarService } from 'services/snackbarService';
import { mapProcedureToForm } from './handleInitialValues';
import SelectCPTModifier from 'components/modals/SelectCPTModifier/SelectCPTModifier';
import useGlobalStyles from 'styles/globalStyles';

const defaultInitialValues = {
  patient_first_name: '',
  patient_last_name: '',
  gender: '',
  date_of_birth: null,
  age: '',
  mrn: '',
  race: '',
  insurance: '',
  insurance_status: '',
  hospital_id: undefined,
  primary_surgeons: [],
  co_surgeons: [],
  procedure_diagnosis: '',
  icds: [],
  icd_id: [],
  date_of_procedure: dayjs(),
  procedure_name: '',
  type: '',
  approach: [],
  position: [],
  minimally_invasive: '',
  navigation: '',
  robotic: '',
  indication: [],
  side: '',
  target: '',
  nerve: '',
  location: [],
  _procedure: [],
  tici_grade: '',
  osteotomy: '',
  _fusion: '',
  interbody_fusion: '',
  extension_to_pelvis: '',
  fusion_levels: '',
  decompression_levels: '',
  morphogenic_protein: '',
  bone_graft: '',
  implant: '',
  implant_type: '',
  _vendors: [],
  vendors: '',
  access: '',
  vascular_closure_device: '',
  duration_of_radiation: '',
  duration_of_procedure: '',
  findings: '',
  complications: '',
  outcome: '',
  cpts: [],
  cpt_modifier: null,
  cpt_modifier_id: null,
  notes: [],
  case_procedures: [],
};

export default function AddCasePage() {
  const patientId = Number(useParams().patientId) ?? undefined;
  const { user, doctor } = useAuth();
  const globalStyles = useGlobalStyles();
  const location = useLocation();
  const initialDate = location.state?.date ? dayjs(location.state.date) : dayjs();
  const [yesNoList] = useState<LabelValueType[]>([
    { key: '1', value: '1', label: 'Yes' },
    { key: '0', value: '0', label: 'No' }
  ]);
  const [images, setImages] = useState<any[]>([]);
  const navigate = useNavigate();

  const speciality = useMemo(() => {
    return user.user_profile[0].speciality?.name;
  }, [user]);

  const { data: patient } = useGetProcedureById(patientId, user.id, {
    enabled: !!patientId,
    onSuccess: (res: any) => {
      reset(mapProcedureToForm(res, isMultipleSelection));
      setImages(res.images);
    },
    onError: (err: any) => {
      console.error('error', err);
    },
    keepPreviousData: true,
    refetchOnWindowFocus: false,
  });

  const methods = useForm<procedureDataType>({
    resolver: yupResolver(addCaseSchema),
    defaultValues: { ...defaultInitialValues, date_of_procedure: initialDate },
  });

  const {
    reset,
    control,
    setValue,
    formState: { isValid },
  } = methods;

  const {
    handleSubmit,
  } = methods;

  const isMultipleSelection = useMemo(() => {
    return isNeurosurgery(speciality) || isInterventional(speciality);
  }, [speciality]);

  // All Lookups
  const { data: genders } = useGetGenders();
  const { data: raceList } = useGetRaceList();
  const { data: insuranceList } = useGetInsuranceList();
  const { data: insuranceStatusList } = useGetInsuranceStatusList();
  const { data: userHospitals, refetch: refetchHospitals } = useGetUserHospitals(user.id);
  const { data: roles, refetch: refetchRoles } = useGetRoles();
  const { data: procedureTypes } = useGetProcedureTypes(speciality);
  const { data: cranialApproachList } = useGetApproachList('Cranial');
  const { data: spineApproachList } = useGetApproachList('Spine');
  const { data: cranialIndicationList } = useGetIndicationList('Cranial');
  const { data: spineIndicationList } = useGetIndicationList('Spine');
  const { data: functionalIndicationList } = useGetIndicationList('Functional');
  const { data: nervesIndicationList } = useGetIndicationList('Peripheral nerves');
  const { data: interIndicationList } = useGetIndicationList('Interventional');
  const { data: interventionalIndicationList } = useGetIndicationList('Interventional Neurology/Neuroradiology');
  const { data: radiologyIndicationList } = useGetIndicationList('Interventional Radiology');
  const { data: interventionalProcedureTypeList } = useGetProcedureList('Interventional');
  const { data: bedsideProcedureTypeList } = useGetProcedureList('Bedside');
  const { data: spineLocationList } = useGetLocationList('Spine');
  const { data: tumorLocationList } = useGetLocationList('Tumor');
  const { data: aneurysmLocationList } = useGetLocationList('Aneurysm');
  const { data: aneurysmLocationInterList } = useGetLocationList('AneurysmInter');
  const { data: hematomaLocationList } = useGetLocationList('Hematoma');
  const { data: fractureLocationList } = useGetLocationList('Fracture');
  const { data: hydrocephalusLocationList } = useGetLocationList('Hydro');
  const { data: avmLocationList } = useGetLocationList('AVM');
  const { data: bleedingLocationList } = useGetLocationList('Bleeding');
  const { data: dissectionLocationList } = useGetLocationList('Dissection');
  const { data: strokeLocationList } = useGetLocationList('Stroke');
  const { data: sideList } = useGetSideList();
  const { data: ticiGradeList } = useGetTICIGrade();
  const { data: osteotomyList } = useGetOsteotomyList();
  const { data: vendorsList } = useGetVendorsList();
  const { data: positionList } = useGetPositionList();

  const sharedProps = useMemo(() => ({
    genders,
    raceList,
    insuranceList,
    insuranceStatusList,
    userHospitals,
    cranialApproachList,
    spineApproachList,
    procedureTypes,
    speciality,
    sideList,
    ticiGradeList,
    osteotomyList,
    vendorsList,
    positionList,
    interventionalIndicationList,
    radiologyIndicationList,
    cranialIndicationList,
    spineIndicationList,
    functionalIndicationList,
    nervesIndicationList,
    interIndicationList,
    interventionalProcedureTypeList,
    bedsideProcedureTypeList,
    tumorLocationList,
    aneurysmLocationList,
    hematomaLocationList,
    fractureLocationList,
    hydrocephalusLocationList,
    spineLocationList,
    avmLocationList,
    bleedingLocationList,
    aneurysmLocationInterList,
    strokeLocationList,
    dissectionLocationList,
    yesNoList,
    isMultipleSelection,
    refetchHospitals,
    roles,
  }), [
    genders, raceList, insuranceList, insuranceStatusList, userHospitals,
    cranialApproachList, spineApproachList, procedureTypes, speciality,
    sideList, ticiGradeList, osteotomyList, vendorsList, positionList,
    interventionalIndicationList, radiologyIndicationList,
    cranialIndicationList, spineIndicationList, functionalIndicationList,
    nervesIndicationList, interIndicationList,
    interventionalProcedureTypeList, bedsideProcedureTypeList,
    tumorLocationList, aneurysmLocationList, hematomaLocationList,
    fractureLocationList, hydrocephalusLocationList, spineLocationList,
    avmLocationList, bleedingLocationList, aneurysmLocationInterList,
    strokeLocationList, dissectionLocationList,
    yesNoList, isMultipleSelection, refetchHospitals, roles,
  ]);

  const newImages = useMemo(() => {
    return images.filter(item => !item?.id);
  }, [images]);

  const updateImagesMutation = useUpdateProcedureImages(
    (data: any) => { },
    (error: any) => console.log('error update images', error),
  )

  const createMutation = useCreateProcedure(
    async (res: any) => {
      try {
        const { data, status_code: statusCode } = res;
        if (statusCode == 200) {
          if (newImages?.length > 0) {
            await updateProcedureImages(data.id);
          }
          snackbarService.showSnackbar('Patient has been added successfully', 'success');
          navigate(`/patients/${data.id}`);
        } else {
          snackbarService.showSnackbar(data?.message, 'error');
        }
      } catch (error) {
        snackbarService.showSnackbar(res?.message, 'error');
      }
    },
    () => { },
  )

  const updateMutation = useUpdateProcedure(
    async (res: any) => {
      const { data, status_code: statusCode } = res;
      if (statusCode == 200) {
        if (newImages?.length > 0) {
          await updateProcedureImages(patientId);
        }
        snackbarService.showSnackbar('Patient has been updated successfully', 'success');
        navigate(`/patients/${patientId}`);
      } else {
        snackbarService.showSnackbar(data, 'error');
      }
    },
    () => { },
  )

  const updateProcedureImages = async (pId?: number) => {
    await updateImagesMutation.mutateAsync({
      userId: doctor?.id ?? user.id,
      procedureId: pId ?? patientId,
      images: newImages,
    });
  }

  const isSubmitting = useMemo(() => {
    return createMutation.isLoading || updateImagesMutation.isLoading || updateMutation.isLoading;
  }, [createMutation.isLoading, updateImagesMutation.isLoading, updateMutation.isLoading]);

  const onSubmit = async (data: any) => {
    if (patientId) {
      await updateMutation.mutateAsync({
        userId: doctor?.id ?? user.id,
        data: data,
        procedureId: patientId,
      });
    } else {
      await createMutation.mutateAsync({
        userId: doctor?.id ?? user.id,
        data: data
      });
    }
  };

  const cpts = useWatch({
    control,
    name: `cpts`,
  });

  const cptModifier = useWatch({
    control,
    name: `cpt_modifier`,
  });


  useEffect(() => {
    reset({ ...defaultInitialValues, date_of_procedure: initialDate });
  // initialDate is derived from location.state which doesn't change after mount
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId, reset]);

  return (
    <Box className={globalStyles.container} p={3}>
      <FormProvider {...methods}>
        <Box
          component="form"
          onSubmit={handleSubmit(onSubmit)}
        >

          {/* Patient Information */}
          <Box>
            <PatientInformation shared={sharedProps} />
          </Box>

          {/* Diagnosis */}
          <Box mt={3}>
            <Diagnosis />
          </Box>

          {/* ICD-9/10 */}
          <Box mt={3}>
            <SelectICDs name={'icds'} title={'Diagnosis (ICD-9/10)'} />
          </Box>

          {/* Procedure Details */}
          <Box mt={3}>
            <ProcedureDetails shared={sharedProps} />
          </Box>

          <Box mt={3}>
            <SelectCPTs
              name={'cpts'}
              title={'CPT Codes and RVUs'}
              procedureNamePath={'procedure_name'}
            />
          </Box>


          {Array.isArray(cpts) && cpts.length > 0 && (
            <Box mt={3}>
              <SelectCPTModifier
                title="CPT Modifier"
                selectedModifier={cptModifier}
                setCPTModifier={(modifier: any) => {
                  setValue(`cpt_modifier`, modifier || null, {
                    shouldDirty: true,
                  });
                  setValue(`cpt_modifier_id`, modifier?.id || null);
                }
                }
                cpts={cpts}
              />
            </Box>
          )}

          <Box mt={3}>
            <CaseProcedures
              shared={sharedProps}
              defaultProcedureDate={location.state?.date ? initialDate : undefined}
            />
          </Box>

          <Box mt={3}>
            <CaseNotes />
          </Box>

          <Box mt={3}>
            <CaseImages images={images} setImages={setImages} />
          </Box>

          <Box my={3}>
            <CustomButton
              text={patientId ? "Update Patient" : "Add Patient"}
              fullWidth
              type='submit'
              loading={isSubmitting}
              // disabled={!isValid}
            />
          </Box>
        </Box>

      </FormProvider>
    </Box>
  );
}
